// The gift letter (2026-10-19, Chris): the sender's sent page and the Journal's
// Sent tab. Pure helpers, so the words and the shapes are tested on their own.
// The server's data: `reading-preview` (her version in the main fields, his in
// `own`, `cards[].phrase`, `gift.history`) and `care-package/sent/`
// (`history` per package), both from one backend helper
// (`care_package.history`), so the page and the Journal never disagree.
import { relativeTime, morningOf, exactTime } from '@/utils/when'
import { timelineJoinedTitle } from '@/utils/coven-ring'

// ── The switch's two labels. Chris was unsure of the words (2026-10-19), so
// they live here and only here. "Your reading" is first and always the default.
export const VERSION_LABELS = {
  mine: () => 'Your reading',
  hers: (name) => `${name || 'Their'}'s version`,
}

// ── What the history may show. A pending privacy decision may make her rating
// reach the sender only when she chooses to share it: set `rated` (or `joined`)
// to false and it leaves the Journal's timeline and the sent page's status. The
// server has the same switch (care_package.HISTORY_SHOWS), which also keeps the
// data from being sent at all.
export const TIMELINE_SHOWS = { rated: true, joined: true }

// 'readingPast' / 'past' / 'outer_influence' -> 'Past' / 'Past' / 'Outer Influence'
export function positionName (card) {
  if (card && card.position) return card.position
  const r = ((card && card.reference) || '').replace(/^reading(?=[A-Z_])/, '')
  if (!r || /^pos\d+$/i.test(r)) return ''
  return r.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (m) => m.toUpperCase()).trim()
}

// The summary's label names the count: "All four together", "All three
// together", "Both together"; a one-card reading has no "together".
export function togetherLabel (n) {
  if (n >= 5) return 'All together'
  if (n === 4) return 'All four together'
  if (n === 3) return 'All three together'
  if (n === 2) return 'Both together'
  return ''
}

const capital = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// The two versions of one letter, from the reading-preview payload. The
// cards, picture and note are the same; the words differ. `mine` is null for a
// package with one version (sent before her version existed, R4.12).
export function letterVersions (data) {
  const d = data || {}
  const cards = d.cards || []
  const base = (c) => ({
    name: c.name || '', reversed: !!c.reversed, image_url: c.image_url || '', position: positionName(c),
  })
  const hers = {
    title: d.summary_title || '',
    // The picture's line written to them (picture-caption 2026-10-30); '' on
    // an older package: never the sender's.
    caption: d.image_caption || '',
    summary: d.reading_summary || '',
    cards: cards.map((c) => ({ ...base(c), phrase: c.phrase || '', text: c.reading_text || c.lens_meaning || c.card_meaning || '' })),
  }
  const own = d.own
  if (!own) return { hers, mine: null }
  const mineCards = cards.map((c, i) => {
    const o = (own.cards || [])[i] || {}
    return { ...base(c), phrase: o.tagline || '', text: o.text || hers.cards[i].text }
  })
  return { hers, mine: { title: own.title || hers.title, caption: own.caption || '', summary: own.summary || hers.summary, cards: mineCards } }
}

// ── The sent page's status band: the current state and the one next step,
// relative to now. Returns { lead, next } (next may be '').
export function statusLine (history, { now = new Date(), revokedNow = false } = {}) {
  const h = history || {}
  const rel = (iso) => relativeTime(iso, now)
  if (revokedNow) return { lead: 'Taken back just now.', next: 'The link no longer opens it.' }
  if (h.status === 'taken_back') return { lead: `Taken back ${rel(h.revoked_at)}.`, next: 'The link no longer opens it.' }
  if (h.status === 'thanked') {
    return h.thanks
      ? { lead: `They thanked you ${rel(h.thanked_at)}:`, quote: h.thanks, next: '' }
      : { lead: `They thanked you ${rel(h.thanked_at)}.`, quote: '', next: '' }
  }
  if (h.status === 'rated' && TIMELINE_SHOWS.rated && h.rated_word) {
    return { lead: 'They said it felt', word: capital(h.rated_word), tail: ` · ${rel(h.rated_at)}.`, next: '' }
  }
  if (h.status === 'opened' || h.status === 'rated') return { lead: `Opened ${rel(h.opened_at)}.`, next: '' }
  // Not opened yet.
  if (h.reminded_at) {
    return { lead: `Not opened yet. One reminder went out ${rel(h.reminded_at)}, in your name.`, next: 'That was the only one.' }
  }
  const sent = rel(h.sent_at)
  const lead = sent === 'a moment ago' ? 'Sent a moment ago. Not opened yet.' : `Sent ${sent}. Not opened yet.`
  if (h.next && h.next.kind === 'reminder') {
    return { lead, next: `If it's still closed ${morningOf(h.next.at, now)}, we'll send one reminder in your name.` }
  }
  return { lead, next: "We'll tell you in Messages when they open it." }
}

// ── The Journal: one pill and the full timeline, exact times.
export function statusPill (history) {
  const h = history || {}
  switch (h.status) {
    case 'taken_back': return { text: 'Taken back', tone: 'muted' }
    case 'thanked': return { text: 'Thanked you', tone: 'gold' }
    case 'rated':
      if (TIMELINE_SHOWS.rated && h.rated_word) return { text: `Felt it: ${capital(h.rated_word)}`, tone: 'gold' }
      return { text: 'Opened', tone: 'gold' }
    case 'opened': return { text: 'Opened', tone: 'gold' }
    default: return { text: 'Not opened', tone: 'lilac' }
  }
}

function sentHow (detail) {
  const d = detail || {}
  if (d.kind === 'link') return 'Sent by link'
  if (d.kind === 'email') return d.to ? `Sent to ${d.to}` : 'Sent by email'
  if (d.kind === 'phone') return d.to ? `Sent by text to ${d.to}` : 'Sent by text'
  if (d.kind === 'member') return 'Sent to their Messages'
  return 'Sent'
}

// [{ key, title, when, quote?, note?, pending? }] in time order. A reminder that
// went while it is still closed ends on "Nothing more will be sent".
export function timelineRows (history) {
  const h = history || {}
  const rows = []
  for (const [i, e] of (h.timeline || []).entries()) {
    const when = exactTime(e.at)
    const key = `${e.kind}-${i}`
    switch (e.kind) {
      case 'sent': rows.push({ key, title: sentHow(e.detail), when }); break
      case 'opened': rows.push({ key, title: 'Opened', when }); break
      case 'rated':
        if (TIMELINE_SHOWS.rated) rows.push({ key, title: (e.detail && e.detail.word) ? `Said it felt ${capital(e.detail.word)}` : 'Rated it', when })
        break
      case 'thanked': rows.push({ key, title: 'Thanked you', when, quote: (e.detail && e.detail.note) || '' }); break
      case 'joined':
        if (TIMELINE_SHOWS.joined) {
          // 2026-10-26 (circle consent): a join adds nobody to a circle, and
          // the carats show on Quest (the invite wish), not here.
          rows.push({ key, title: timelineJoinedTitle(), when })
        }
        break
      case 'reminder': rows.push({ key, title: 'One reminder sent, in your name', when }); break
      case 'taken_back': rows.push({ key, title: 'Taken back', when }); break
      default: break
    }
  }
  if (h.reminded_at && h.status === 'not_opened') {
    rows.push({ key: 'no-more', title: 'Nothing more will be sent', pending: true })
  }
  if (h.next && h.next.kind === 'reminder' && h.status === 'not_opened') {
    rows.push({ key: 'next', title: 'One reminder, if it is still closed', when: exactTime(h.next.at), pending: true })
  }
  return rows
}
